import { ColDef, ColGroupDef } from 'ag-grid-enterprise'

import { parseDate } from '../../utils/dates'
import { objectFilter } from './filter-converters'
import { AG_GRID_FILTER_TYPES, ValuesOf } from './table.types'

const getColumnClass = (column: ColDef) => {
  if (column.align === 'right') {
    return {
      headerCss: ['ag-cell-align-right'],
      cellCss: [],
    }
  }

  return {
    headerCss: [],
    cellCss: [],
  }
}

const getFilterType = ({ filterType, type }: ColDef) => {
  // if filter type is explicitly set.
  if (filterType) {
    return (
      {
        [AG_GRID_FILTER_TYPES.Numerical]: AG_GRID_FILTER_TYPES.Numerical,
        [AG_GRID_FILTER_TYPES.Date]: AG_GRID_FILTER_TYPES.Date,
        [AG_GRID_FILTER_TYPES.Custom]: AG_GRID_FILTER_TYPES.Custom,
        [AG_GRID_FILTER_TYPES.Text]: AG_GRID_FILTER_TYPES.Text,
        [AG_GRID_FILTER_TYPES.NoFilter]: false,
      }[filterType] ?? AG_GRID_FILTER_TYPES.Text
    )
  }

  // otherwise infer from subtype.
  // if (type) {
  //   return (
  //     aggridFilterTypes[filterTypeMapping[type as string]] ??
  //     AG_GRID_FILTER_TYPES.Text
  //   )
  // }
  return AG_GRID_FILTER_TYPES.Text
}

const getFilterParams = (
  filterType: ValuesOf<typeof AG_GRID_FILTER_TYPES> | boolean,
  filterCustomKey?: string,
): { comparator?: any } => {
  let filterParams = {}

  switch (filterType) {
    case AG_GRID_FILTER_TYPES.Numerical:
      filterParams = {
        allowedCharPattern: '\\d\\-\\,',
        filterOptions: [
          {
            displayKey: 'equals',
            displayName: 'Equals',
            predicate: ([fv1]: [fv1: any], cellValue: any) => {
              return fv1 === Number(cellValue?.value ?? cellValue)
            },
          },
          'notEqual',
          'lessThan',
          'lessThanOrEqual',
          'greaterThan',
          'greaterThanOrEqual',
          'inRange',
          'blank',
          'notBlank',
        ],
        numberParser: (text: any) => {
          const number =
            text == null ? null : parseFloat(text.replace(',', '.'))
          return number
        },
        comparator: (a: any, b: any) => {
          const aNumber = Number(a?.value ?? a)
          const bNumber = Number(b?.value ?? b)
          if (aNumber === bNumber) return 0
          return aNumber > bNumber ? 1 : -1
        },
      }
      break

    case AG_GRID_FILTER_TYPES.Date:
      filterParams = {
        comparator: dateComparator,
        // comparator: (filterLocalDateAtMidnight: Date, cellValue: string) => {
        //   const dateAsString = cellValue
        //   if (dateAsString == null) return -1
        //   const dateMomentObject = moment(cellValue, 'DD/MM/YYYY')
        //   const cellDate = dateMomentObject.toDate()

        //   if (filterLocalDateAtMidnight.getTime() === cellDate.getTime()) {
        //     return 0
        //   }
        //   if (cellDate < filterLocalDateAtMidnight) {
        //     return -1
        //   }
        //   if (cellDate > filterLocalDateAtMidnight) {
        //     return 1
        //   }
        //   return -1
        // },
        browserDatePicker: true,
      }
      break

    case AG_GRID_FILTER_TYPES.Custom:
      filterParams = {
        comparator: (a: any, b: any) => {
          if (parseInt(a[filterCustomKey!]) === parseInt(b[filterCustomKey!]))
            return 0
          return parseInt(a[filterCustomKey!]) > parseInt(b[filterCustomKey!])
            ? 1
            : -1
        },
      }
      break

    default:
      filterParams = {
        filterOptions: [
          'contains',
          'notContains',
          'equals',
          'notEqual',
          'startsWith',
          'endsWith',
          'blank',
          'notBlank',
          {
            displayKey: 'in',
            displayName: 'In',
            predicate: (filterValues: any[], cellValue: any) => {
              const cellValueStr = cellValue.toString()
              return filterValues.includes(cellValueStr)
            },
            numberOfInputs: 1,
            inputType: 'text',
          },
        ],
      }
      break
  }
  return filterParams
}

const CELL_TYPE_DEFAULT_OPTIONS: Partial<Record<string, ColDef>> = {
  Int: {
    align: 'right',
  },
  Float: {
    align: 'right',
  },
  number: {
    align: 'right',
  },
}

export const dateComparator = (valueA: string, valueB: string) => {
  const dateA = parseDate(valueA)
  const dateB = parseDate(valueB)

  if (!dateA || !dateB) return 0

  if (dateA < dateB) return -1
  if (dateA > dateB) return 1
  return 0
}

/** Single-line headers for column groups (defaultColDef does not apply to groups). */
const HEADER_NO_WRAP: Pick<ColDef, 'wrapHeaderText' | 'autoHeaderHeight'> = {
  wrapHeaderText: false,
  autoHeaderHeight: false,
}

export const setupColumnDefs = (
  columnDefs: (ColDef | ColGroupDef)[] = [],
): (ColDef | ColGroupDef)[] => {
  return columnDefs.map((column): ColDef | ColGroupDef => {
    if ('children' in column && column.children != null) {
      const { type, ...columnWithoutType } = column as ColGroupDef & {
        type?: unknown
      }
      return {
        ...columnWithoutType,
        children: setupColumnDefs(column.children),
        ...objectFilter(column, val => typeof val !== 'undefined'),
        ...HEADER_NO_WRAP,
      }
    }

    const classAtr = getColumnClass(column)
    const filterType = getFilterType(column)
    const filterParams = getFilterParams(
      filterType,
      (column as any).filterCustomKey,
    )

    const defaults = column.dataType
      ? (CELL_TYPE_DEFAULT_OPTIONS[column.dataType] ?? {})
      : {}

    // Remove type property if it exists - AG Grid doesn't recognize our custom types
    // Our type system is handled separately via ContentNodes and DataTypeConfigs
    const { type, ...columnWithoutType } = column

    return {
      ...defaults,
      ...columnWithoutType,
      colId: column.colId ?? column.field,
      headerClass: classAtr.headerCss,
      // Not applied when only defaultColDef is used.
      sortable: true,
      filter: false, // Temporarily disabled
      filterParams: {
        ...filterParams,
        maxNumConditions: 1,
      },
      comparator: filterParams?.comparator || undefined,
      cellClass: classAtr.cellCss,
      context: column.context ?? {},
      ...objectFilter(column, val => typeof val !== 'undefined'),
      ...HEADER_NO_WRAP,
    }
  })
}
